import React from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'

import Typography from '../../UniformComponents/Typography'
import OneByOneLayout from './layouts/1x1'
import TwoByOneLayout from './layouts/2x1'
import ThreeByOneLayout from './layouts/3x1'
import OneByTwoLayout from './layouts/1x2'
import FourByOneLayout from './layouts/4x1'
import TitleSlide from './layouts/TitleSlide'
import SectionHeader from './layouts/SectionHeader'
import TwoByTwoLayout from './layouts/2x2'
import Verbatim from './layouts/Verbatim'
import Derived from './layouts/Derived'
import Specification from './layouts/Specification'
import Inherited from './layouts/Inherited'
import Swot from './layouts/Swot'
import Summary from './layouts/Summary'
import CrossbreakLayout from './layouts/Crossbreak'
import LMX from './layouts/LMX'
import OSS from './layouts/OSS'
import BenchmarkExplainer from './layouts/BenchmarkExplainer'
import MatrixExplainer from './layouts/MatrixExplainer'
import NPSExplainer from './layouts/NPSExplainer'
import CorrelationExplainer from './layouts/CorrelationExplainer'
import ContactDetails from './layouts/ContactDetails'
import SectionSummary from './layouts/SectionSummary'
import FilterPill from './layouts/components/FilterPill'
import FilterBreakdownPopover from './layouts/components/FilterBreakdownPopover'
import { getPopoverPlacement, ICON_ONLY_TEMPLATES, countFilterRules } from './layouts/components/popoverPlacement'

const TITLE_UNEDITABLE_TEMPLATES = ['title', 'matrixExplainer', 'benchmarkExplainer', 'contactDetails', 'npsExplainer']

const Slide = ({ store }) => {
    const {
        handleOpenSlideSettingsModal, deleteSlide, duplicateSlide,
    } = store

    // section only affects verbatim questions
    const question = store.activeSlide?.question.get(0)
    const template = store.activeSlide?.template
    const pillMode = ICON_ONLY_TEMPLATES.has(template) ? 'icon-only' : 'labelled'
    const isPopoverOpen = store.openFilterPopoverPosition === 0
    const popoverPlacement = getPopoverPlacement(template, 0)
    const filterRuleGroup = question?.filter?.question
    const hasFilters = countFilterRules(filterRuleGroup) > 0

    const handleFilter = () => {
        store.handleOpenFilterModal(0)
    }

    const handleTogglePopover = () => {
        if (isPopoverOpen) {
            store.closeFilterPopover()
        } else {
            store.setOpenFilterPopover(0)
        }
    }

    const renderSlideLayout = () => {
        if (!store.activeSlide) return null

        switch (store.activeSlide?.template) {
            case '1x1':
                return <OneByOneLayout store={store} />
            case '2x1':
                return <TwoByOneLayout store={store} />
            case '2x2':
                return <TwoByTwoLayout store={store} />
            case '3x1':
                return <ThreeByOneLayout store={store} />
            case '1x2':
                return <OneByTwoLayout store={store} />
            case '4x1':
                return <FourByOneLayout store={store} />
            case 'verbatim':
                return <Verbatim store={store} />
            case 'derived':
                return <Derived store={store} />
            case 'crosstab':
                return <CrossbreakLayout store={store} />
            case 'title':
                return <TitleSlide store={store} />
            case 'lmx':
                return <LMX store={store} />
            case 'oss':
                return <OSS store={store} />
            case 'swot':
                return <Swot store={store} />
            case 'summary':
                return <Summary store={store} />
            case 'inherited':
                return <Inherited store={store} />
            case 'sectionSummary':
                return <SectionSummary store={store} />
            case 'recommendations':
                return null
            case 'sectionHeader':
                return <SectionHeader store={store} />
            case 'specification':
                return <Specification store={store} />
            case 'benchmarkExplainer':
                return <BenchmarkExplainer store={store} />
            case 'matrixExplainer':
                return <MatrixExplainer store={store} />
            case 'npsExplainer':
                return <NPSExplainer store={store} />
            case 'correlationExplainer':
                return <CorrelationExplainer store={store} />
            case 'contactDetails':
                return <ContactDetails store={store} />
            default:
                return null
        }
    }

    const isStyledSlide = ['title', 'verbatim', 'benchmarkExplainer', 'contactDetails', 'sectionSummary'].includes(store.activeSlide?.template)
    const isTitleSlide = ['title', 'sectionHeader', 'specification', 'swot', 'summary', 'benchmarkExplainer', 'contactDetails', 'sectionSummary'].includes(store.activeSlide?.template)
    const isNonChartSlideTitle = ['swot', 'summary'].includes(store.activeSlide?.template)
    const slideTitle = isNonChartSlideTitle ? store.activeSlide.title : store.activeSlideTitle

    return (
        <div className={`pp-slide ${isStyledSlide ? 'styled-slide' : ''} ${isTitleSlide ? 'pp-title-slide' : ''}`}>
            {!['title', 'sectionHeader', 'benchmarkExplainer', 'contactDetails', 'sectionSummary'].includes(store.activeSlide?.template) && (
                <div className="pp-slide-title-container">
                    <div>
                        <Typography
                            type="tiny"
                            color="primary"
                        >
                            {store.activeSlideRespondentTypes}
                        </Typography>
                        <Typography
                            type="b"
                            color="white"
                        >
                            {slideTitle}
                        </Typography>
                    </div>

                    <img
                        src="https://cdn.explori.com/images/logo_explori.png"
                        alt="explori logo"
                        style={{ height: 30 }}
                    />
                </div>
            )}

            {renderSlideLayout()}

            <div className="pp-slide-controls">
                {!store.isTemplate && store.activeSlide?.template === 'verbatim' && (
                    <div className="pp-filter-pill-anchor">
                        <FilterPill
                            ruleGroup={filterRuleGroup}
                            mode={pillMode}
                            isOpen={isPopoverOpen}
                            onToggle={handleTogglePopover}
                        />
                        {isPopoverOpen && (
                            <FilterBreakdownPopover
                                ruleGroup={filterRuleGroup}
                                placement={popoverPlacement}
                                onClose={store.closeFilterPopover}
                                onEdit={handleFilter}
                            />
                        )}
                    </div>
                )}

                {!store.isTemplate && !hasFilters && store.activeSlide?.template === 'verbatim' && (
                    <div className="pp-slide-edit" onClick={handleFilter}>
                        <i className="fas fa-filter" />
                    </div>
                )}

                {!TITLE_UNEDITABLE_TEMPLATES.includes(store.activeSlide?.template) && (
                    <div className="pp-slide-edit" onClick={handleOpenSlideSettingsModal}>
                        <i className="fas fa-heading" />
                    </div>
                )}

                {!['title', 'sectionHeader', 'summary', 'sectionSummary'].includes(store.activeSlide?.template) && (
                    <div className="pp-slide-edit" onClick={duplicateSlide}>
                        <i className="fas fa-copy" />
                    </div>
                )}

                {store.activeSlide?.template !== 'title' && (
                    <div className="pp-slide-delete" onClick={() => deleteSlide('slide')}>
                        <i className="fas fa-trash" />
                    </div>
                )}
            </div>
        </div>
    )
}

export default observer(Slide)
